/**
 * Created by rathompson on 1/19/22
 * File: InstanceCard
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useContext, useMemo } from 'react'
import { parseISO, isValid, parse, format } from 'date-fns'
import { BUTTON_ERROR, BUTTON_LAUNCH } from '../../../../constants/LabelConstants'
import LoadingCard from '../LoadingCard'
import LicenseCardHeader from '../../../LicenseCard/LicenseCardHeader'
import {
  APP_ID_LOGGING_SERVICE,
  APP_ID_NG_CASB,
  APP_ID_PRISMA_ACCESS_EDITION,
  APP_ID_SDWAN,
  CTX_CDL,
} from '../../../../../common/constants/GlobalConstants'
import { Button, noop } from '@panwds/react-ui'
import {
  MOBILE_USERS_LABEL,
  REMOTE_NETWORK_LABEL, SKU_SERVICE_LOCATION_LOCAL,
  SKU_SERVICE_LOCATION_WORLDWIDE,
} from '../../../constants/SubscriptionConstant'
import { ARRAY } from '../../../../../common/utils/Utils'
import AppContext from '../../../../../common/context/AppContext'
import { getUserCount } from '../../../../../common/actions/GlobalActions'
import { getAppTitle } from '../../../../util/common'

const PROVISIONING_STATUSES = Object.freeze({
  ERROR: 'error',
  INITIALIZING: 'initializing',
  PROVISIONING: 'provisioning',
  COMPLETE: 'complete'
})

const getExpiry = (instance, license, primaryLicense, licenses) => {
  if (instance?.license_expiration) {
    const parsed = parse(instance?.license_expiration, 'yyyy-MM-dd HH:mm:ss', new Date())
    if (isValid(parsed)) {
      return format(parsed, 'MM/dd/yyyy')
    }
    return format(new Date(instance?.license_expiration), 'MM/dd/yyyy')
  }
  if (license?.license_expiration) {
    return format(parseISO(license?.license_expiration), 'MM/dd/yyyy')
  }
  if (primaryLicense?.license_expiration) {
    return format(parseISO(primaryLicense?.license_expiration), 'MM/dd/yyyy')
  }
  if (ARRAY.hasArray(licenses) && licenses[0].license_expiration) {
    return format(parseISO(licenses[0].license_expiration), 'MM/dd/yyyy')
  }
  return null
}

const getAddons = (license) => {
  return license?.entitlements?.filter(each => each.asset_type === 'addon').map(v => ({ ui_display_name: v.license_display_name }))
}

const getBaseDetails = (instance, instanceLicense, primaryLicense, licenses, apps) => {
  const hideImageForApps = new Set([APP_ID_PRISMA_ACCESS_EDITION, APP_ID_SDWAN, APP_ID_NG_CASB])
  const expiry = getExpiry(instance, instanceLicense, primaryLicense, licenses)
  const hideTitle = instance?.extra?.license_context === CTX_CDL ? true : false
  const imageName = (hideImageForApps.has(instance?.app_id) || hideTitle) ? null : 'addon'
  const showImage = Boolean(imageName)
  const label = getAppTitle(instance, apps[instance?.app_id])
  return {
    expiry,
    imageName,
    showImage,
    label,
    hideTitle
  }
}

const getProductDetails = (instance, licenses, apps) => {

  const primaryLicense = licenses?.find(({ app_id }) => app_id === APP_ID_PRISMA_ACCESS_EDITION || app_id === APP_ID_SDWAN)
  const instanceLicense = licenses?.find(({ app_id }) => app_id === instance?.app_id)

  const details = getBaseDetails(instance, instanceLicense, primaryLicense, licenses, apps)

  if (instance.app_id === APP_ID_PRISMA_ACCESS_EDITION) {
    details.addons = getAddons(primaryLicense)
  }
  if (instance.app_id === APP_ID_SDWAN) {
    const ions = primaryLicense?.entitlements?.reduce((object, entitlement) => {
      if (entitlement.license_display_name === 'Physical IONs' || entitlement.license_display_name === 'Virtual IONs') {
        object[entitlement.license_display_name] = entitlement.purchased_size
      }
      return object
    }, {})
    if (primaryLicense?.app_id === APP_ID_SDWAN) {
      details.addons = getAddons(primaryLicense)
    }
    details.ions = ions
  }
  if (instanceLicense?.entitlements && instance.app_id !== APP_ID_SDWAN) {
    const mobileUsersQuantity = instanceLicense?.entitlements?.find(({ purchased_size, license_type }) => license_type?.includes('-MU') && purchased_size)?.purchased_size
    const remoteNetworkQuantity = instanceLicense?.entitlements?.find(({ purchased_size, license_type }) => license_type?.includes('-RN') && purchased_size)?.purchased_size
    if (mobileUsersQuantity || remoteNetworkQuantity) {
      const mobileUsersLabel = `${mobileUsersQuantity ? `${mobileUsersQuantity} ${MOBILE_USERS_LABEL}` : ''}`
      const remoteNetworkLabel = `${remoteNetworkQuantity ? `${remoteNetworkQuantity} ${REMOTE_NETWORK_LABEL}` : ''}`
      details.quantity = `${mobileUsersLabel} ${mobileUsersLabel ? '&' : ''} ${remoteNetworkLabel}`.trim()
    }
    else if (instanceLicense?.entitlements?.find(({ purchased_size }) => Boolean(purchased_size))) {
      details.quantity = instanceLicense?.entitlements?.find(({ purchased_size }) => Boolean(purchased_size)).purchased_size
      if (instance.app_id === APP_ID_LOGGING_SERVICE) {
        details.quantity = `${details.quantity} TB`
      }
    }
    if (instanceLicense?.app_id === APP_ID_PRISMA_ACCESS_EDITION) {
      const isGlobal = instanceLicense?.entitlements?.find(({ license_type }) => license_type.includes('GBL'))
      details.location = isGlobal ? SKU_SERVICE_LOCATION_WORLDWIDE : SKU_SERVICE_LOCATION_LOCAL
    }
  }
  if (instance?.app_id === APP_ID_NG_CASB) {
    const instanceLicense = instance?.entitlements?.find(({ app_id }) => app_id === instance?.app_id)
    const purchasedSize = instanceLicense?.purchased_size
    details.quantity = purchasedSize ? getUserCount(instanceLicense?.license_type, purchasedSize) : null
  }
  return details
}


const InstanceCard = ({ instance, licenses, progress: _progress, showButton, onLaunchClick, loading }) => {

  const { apps } = useContext(AppContext)
  const productDetails = useMemo(() => {
    return getProductDetails(instance, licenses, apps)
  }, [instance, licenses, apps])

  const hasError = useMemo(() => instance?.provisioning_status === PROVISIONING_STATUSES.ERROR, [instance])

  return (
    <div className='tw-border-b tw-border-solid tw-border-gray tw-border-t-0 tw-border-l-0 tw-border-r-0'>
      <div className='tw-p-3 tw-relative'>
        {loading ? (<LoadingCard />) : (
          <div className='tw-flex tw-justify-between'>
            <LicenseCardHeader {...productDetails} />
            {(showButton || hasError) && (
              <Button
                appearance={hasError ? 'secondary-destructive' : 'secondary'}
                disabled={hasError}
                onClick={onLaunchClick}>{hasError ? BUTTON_ERROR : BUTTON_LAUNCH}</Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

InstanceCard.propTypes = {}

InstanceCard.defaultProps = {
  instance: {},
  licenses: [],
  progress: 0,
  showButton: false,
  onLaunchClick: noop,
  loading: false
}
export default InstanceCard
